import { useRef, useState } from 'react';
import type { NomenclatureDto } from '@eombor/contracts';
import { Plus, X } from 'lucide-react';
import { Button, Dialog, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { NomenclaturePicker } from '../../catalog/ui/NomenclaturePicker.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useCreateBackorderBatch } from '../model/backorder.queries.js';

/**
 * Xato matni — SERVERNIKI, bor bo'lsa.
 *
 * Qat'iy jumla («qaytadan urinib ko'ring») ikki marta yomon bo'lardi:
 * maslahati noto'g'ri (ruxsat yo'qligi qayta urinishdan tuzalmaydi) va
 * serverning aniq javobini butunlay yo'q qilardi. `WriteOffDialog` da
 * aynan shu xato allaqachon tuzatilgan va bu yerda takrorlanmaydi.
 */
const FALLBACK = "Bildirgini yuborib bo'lmadi";

const errorText = (error: unknown): string =>
  error instanceof ApiClientError ? error.message : FALLBACK;

const fieldClass = cn(
  'rounded-[var(--radius-sm)] border border-border-control bg-surface',
  'px-3 py-2 text-base text-text hover:border-border-strong',
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
);

/** Savatdagi bitta qator — mahsulot va uning miqdori. */
interface ItemDraft {
  key: number;
  nomenclature: NomenclatureDto | null;
  freeText: string;
  qty: string;
}

const itemReady = (item: ItemDraft): boolean => {
  const qty = Number(item.qty);
  return (
    (item.nomenclature !== null || item.freeText.trim().length >= 3) &&
    Number.isFinite(qty) &&
    qty > 0
  );
};

/**
 * BILDIRGI OYNASI — qoldiqda YO'Q mahsulotlar uchun ariza, SAVAT ko'rinishida.
 *
 * Xodim bitta ish uchun odatda bir nechta narsa so'raydi. Ombor, muddat,
 * maqsad va izoh bir marta yoziladi, mahsulotlar esa ro'yxat bo'lib
 * qo'shiladi. Server har qatordan ALOHIDA bildirgi yozadi (ular har xil
 * vaqtda keladi), lekin hammasini bitta tranzaksiyada.
 *
 * Mahsulot ikki yo'l bilan ko'rsatiladi va ikkinchisi ataylab bor:
 * katalogda hamma narsa yo'q, xodim esa kerakli narsani BILADI.
 *
 * O'lchov birligi so'ralmaydi. Katalogdan tanlanganida u o'zidan keladi,
 * erkin matnda esa noma'lum — «dona» deb taxmin qilinsa, keyinchalik
 * noto'g'ri miqdorli talabnoma tug'ilardi.
 */
export const BackorderDialog = ({
  open,
  onClose,
  /** Qidiruvda terilgan matn — birinchi qatorning erkin matniga tushadi. */
  initialText = '',
  defaultWarehouseId,
}: {
  open: boolean;
  onClose: () => void;
  initialText?: string;
  defaultWarehouseId?: string;
}) => {
  const warehouses = useWarehouses({ limit: 100 });
  const nextKey = useRef(1);
  const [items, setItems] = useState<ItemDraft[]>([
    { key: 0, nomenclature: null, freeText: initialText, qty: '1' },
  ]);
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId ?? '');
  const [purpose, setPurpose] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [note, setNote] = useState('');

  const create = useCreateBackorderBatch();

  const options = warehouses.data?.items ?? [];
  /* Standart ombor — ro'yxatdagi BIRINCHISI, chunki ro'yxat xodimning
     doirasi bilan filtrlanadi va aksariyat xodimda u bitta bo'ladi. */
  const chosenWarehouse = warehouseId === '' ? (options[0]?.id ?? '') : warehouseId;

  const updateItem = (key: number, patch: Partial<ItemDraft>): void =>
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const addItem = (): void => {
    const key = nextKey.current;
    nextKey.current += 1;
    setItems((current) => [...current, { key, nomenclature: null, freeText: '', qty: '1' }]);
  };

  const removeItem = (key: number): void =>
    setItems((current) => current.filter((item) => item.key !== key));

  const ready =
    items.length > 0 &&
    items.every(itemReady) &&
    chosenWarehouse !== '' &&
    purpose.trim().length >= 3;

  const submit = (): void => {
    if (!ready || create.isPending) return;
    create.mutate(
      {
        warehouseId: chosenWarehouse,
        purpose: purpose.trim(),
        neededBy: neededBy === '' ? null : new Date(neededBy),
        note: note.trim() === '' ? null : note.trim(),
        items: items.map((item) => ({
          /* Katalogdan tanlangan bo'lsa erkin matn YUBORILMAYDI: ikkalasi
             ham saqlansa, «qaysi biri to'g'ri» degan savol tug'ilardi. */
          nomenclatureId: item.nomenclature?.id ?? null,
          freeText: item.nomenclature === null ? item.freeText.trim() : null,
          requestedQty: Number(item.qty),
        })),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('backorder.newTitle')}
      description={t('backorder.newHint')}
      busy={create.isPending}
      onSubmit={submit}
      error={create.isError ? errorText(create.error) : undefined}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('backorder.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} loading={create.isPending}>
            {items.length > 1
              ? t('backorder.sendCount', { count: items.length })
              : t('backorder.send')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-text">{t('backorder.items')}</span>

          {items.map((item, index) => (
            <div
              key={item.key}
              className="flex flex-col gap-3 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3"
            >
              <div className="flex items-center gap-2">
                <span className="flex-1 text-xs font-semibold text-text-muted">
                  {t('backorder.itemN', { n: index + 1 })}
                </span>
                {/* Oxirgi qator o'chirilmaydi: bo'sh savatni yuborib bo'lmaydi,
                    o'rniga oynani yopish bor. */}
                {items.length > 1 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(item.key)}
                    aria-label={t('backorder.removeItem', { n: index + 1 })}
                  >
                    <X size={14} aria-hidden />
                  </Button>
                ) : null}
              </div>

              <NomenclaturePicker
                value={item.nomenclature}
                onSelect={(nomenclature) => updateItem(item.key, { nomenclature })}
                label={t('backorder.product')}
                hint={t('backorder.productHint')}
              />

              {/* Erkin matn katalog tanlangach YASHIRILADI, o'chirilmaydi:
                  odam fikridan qaytsa, yozgani joyida turishi kerak. */}
              <div className="flex flex-wrap items-start gap-3">
                {item.nomenclature === null ? (
                  <label className="flex min-w-48 flex-1 flex-col gap-1.5">
                    <span className="text-sm font-medium text-text">{t('backorder.freeText')}</span>
                    <input
                      value={item.freeText}
                      onChange={(event) => updateItem(item.key, { freeText: event.target.value })}
                      maxLength={200}
                      placeholder={t('backorder.freeTextPlaceholder')}
                      className={fieldClass}
                    />
                  </label>
                ) : null}

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-text">{t('backorder.qty')}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={item.qty}
                    onChange={(event) => updateItem(item.key, { qty: event.target.value })}
                    className={cn(fieldClass, 'w-32')}
                  />
                  {/* Birlik faqat katalogdan tanlanganda ma'lum. */}
                  {item.nomenclature === null ? null : (
                    <span className="text-xs text-text-muted">{item.nomenclature.unit}</span>
                  )}
                </label>
              </div>
            </div>
          ))}

          <Button variant="ghost" size="sm" className="self-start" onClick={addItem}>
            <Plus size={14} aria-hidden />
            {t('backorder.addItem')}
          </Button>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex min-w-56 flex-1 flex-col">
            {/* Uch holat AJRATILADI: yuklanmoqda, xato va haqiqatan bo'sh. */}
            <Select
              label={t('backorder.warehouse')}
              value={chosenWarehouse}
              onChange={(event) => setWarehouseId(event.target.value)}
              disabled={warehouses.isPending}
              options={options.map((warehouse) => ({
                value: warehouse.id,
                label: warehouse.name,
              }))}
              {...(warehouses.isPending
                ? { hint: t('backorder.warehouseLoading') }
                : warehouses.isError
                  ? { error: t('backorder.warehouseError') }
                  : options.length === 0
                    ? { error: t('backorder.warehouseNone') }
                    : {})}
            />
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-text">{t('backorder.neededBy')}</span>
            <input
              type="date"
              value={neededBy}
              onChange={(event) => setNeededBy(event.target.value)}
              className={cn(fieldClass, 'w-44')}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('backorder.purpose')}</span>
          <textarea
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            rows={2}
            maxLength={500}
            placeholder={t('backorder.purposePlaceholder')}
            className={cn(fieldClass, 'resize-y')}
          />
          {/* Skladchi ta'minotga buyurtma berayotganda AYNAN shuni o'qiydi. */}
          <span className="text-xs text-text-muted">{t('backorder.purposeHint')}</span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('backorder.note')}</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            maxLength={500}
            className={cn(fieldClass, 'resize-y')}
          />
        </label>
      </div>
    </Dialog>
  );
};
